import {useState, useEffect, useRef} from 'react';
import styles from '../../style.module.scss';
import {TargetKeywordsType} from '@/store/content-optimizer/TargetKeywordsModel';
import {CaretDownOutlined} from '@ant-design/icons';
import {
  Form,
  Typography,
  Progress,
  Popover,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {Button, CommonInput, SkeletonHorizontalLoaderGray} from '@/components/common-components/components';
import isEmpty from 'lodash/isEmpty';
import {Globe} from 'react-feather';
import Flag from '@/components/common-components/components/flag';
import {useTranslation} from 'next-i18next';
import {CurrentPageStore, targetKeywords} from '@/store/content-optimizer/currentPage';
import {getProgressColor, getInverseProgressColor} from '@/utils/colors';
import {useStore} from '@/store/root-store';
import debounce from 'lodash/debounce';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowLeft, faGlobe, faInfoCircle, faMapMarkerAlt, faPen, faTimes} from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import {formatNumber} from '@/utils/number';
import {
  // faChartBar,
  faCheck, faSearch, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {observer} from 'mobx-react-lite';
import {SCA_PAGE_UPDATE_KEYWORD_LOCATION} from '@/constants/events';

import {MixPanel} from '@/utils/mixpanel';
import {faLocationDot} from '@fortawesome/pro-solid-svg-icons';
import {costPerClickFormatter, searchVolumeFormatter} from '../../../../EditorSection/content-editor/utils';


interface Props {
  kw: TargetKeywordsType;
  idx: number;
  store: CurrentPageStore;
  keywordsFormProps?: targetKeywords;
  editKeywords: (keywords: string[]) => Promise<void>;
  callback?: (keywords: string[]) => void;
}


const useClickedOnPageHandler = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = event => {
      event.stopPropagation();
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [ref]);
};


export const SingleKeywordDataCard = observer(({kw, idx, keywordsFormProps, store, callback}: Props) => {
  const {countries: {getSpecificCountryByCode}, contentOptimizer: {currentPage: {isPublic}}} = useStore('');
  const {Text} = Typography;
  const {t} = useTranslation('landing-page-optimizer');
  const Input = CommonInput;
  const [form] = Form.useForm();
  const updateKeyword = Form.useWatch('keyword', form);
  const {settings: {customer: {getCustomerQuota, profile: {quotaUtilization}}}, plans: {showSidebarPaymentDrawer}} = useStore('');
  const {keywordResearcherV2: {postNewKeywords, keywordDetails: {loadKeywordDetailList, loadKeywordDetails, setDetailsRepolling}}} = useStore('');
  const [isKwNameEdit, setIsKwNameEdit] = useState(false);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [isEditDefaultLocation, setIsEditDefaultLocation] = useState(false);
  const popRef = useRef(null);


  const [showKwErrorMsg, setShowKwErrorMsg] = useState({
    showMessage: false,
    message: '',
  });


  useClickedOnPageHandler(popRef, async () => {
    await new Promise(r => setTimeout(r, 100));
    setShowLocationSelector(false);
  });

  const removeTargetKeywords = async () => {
    const keywordsArray = store.feTargetKeywords?.filter((feKw, index) => index !== idx);
    store.updateFeTargetKeywords(keywordsArray);

    let payload;
    if (kw.locationId && kw.location) {
      payload = {
        keyword: kw.keyword,
        countryCode: '',
        locationId: kw.locationId,
        location: kw.location,
      };
    } else if (kw.countryCode) {
      payload = {
        keyword: kw.keyword,
        countryCode: kw.countryCode,
        locationId: null,
        location: null,
      };
    } else if (!kw.locationId && !kw.countryCode) {
      payload = {
        keyword: kw.keyword,
        countryCode: '',
        locationId: null,
        location: null,
      };
    }
    try {
      await store?.editKeywords(payload, 'delete');
      getCustomerQuota();
    } catch (err) {
      if (err?.response?.status === 429) {
        showSidebarPaymentDrawer(null, {title: 'You need more Content Genius focus terms quota to continue', subtitle: '', text: `Current limit: ${quotaUtilization?.ca?.allowedFocusTerms?.total} lookups`});
      }
      return Promise.reject(err);
    }
  };


  useEffect(() => {
    if (!isEmpty(keywordsFormProps)) {
      form.setFieldsValue(kw);
    }
  }, [kw, form]);

  const postAndGetData = async (keyword, countryCode, locationId) => {
    const data = {'name': keyword, 'keywords': [`${keyword}`], 'is_recent_search': true};
    if (locationId) {
      data['location_id'] = locationId;
    } else {
      data['country_code'] = countryCode ? countryCode : '';
    }
    const response = await postNewKeywords(data);
    setDetailsRepolling(true);
    if (response?.id && response?.keywords?.length) {
      loadKeywordDetailList(response.id);
      loadKeywordDetails(response.id, response?.keywords[0], response?.locationId, response?.countryCode);
    }
  };

  const kwClickHandler = (kw: string) => {
    const keyword = [...store.content.targetKeywords].filter(item => item.keyword === kw)[0];
    const countryCode = keyword?.countryCode || '';
    const location = keyword?.locationId || null;
    store.setIsDrawerFromCa(true);
    store.setActiveTargetKw(kw);
    store.setIsOpenKeywordDrawer(true);
    postAndGetData(kw, countryCode, location);
  };


  const onFinish = async (values: { [key: string]: string }) => {
    if ('location-search' in values) return;

    const formKeyword = Object.values(values)
      .filter(item => item !== undefined && item !== '');


    if (formKeyword[0]?.length > 80 || formKeyword[0]?.split(' ')?.length > 10) {
      setShowKwErrorMsg({
        showMessage: true,
        message: 'Max 10 words or 80 char. allowed',
      });
      return;
    }

    if (formKeyword[0] === kw.keyword || store.feTargetKeywords?.filter(kw => kw.keyword.toLowerCase() === formKeyword[0].trim().toLowerCase()).length > 0) {
      setShowKwErrorMsg({
        showMessage: true,
        message: 'This keyword already exists.',
      });
      return;
    }

    const formatArray = [...store.feTargetKeywords];

    const preparedKeyword = {
      keyword: formKeyword[0].trim().toLowerCase(),
      countryCode: formatArray[idx].countryCode,
      location: formatArray[idx].location,
      locationId: formatArray[idx].locationId,
      id: formatArray[idx].id,
    };

    formatArray[idx] = preparedKeyword;

    store.updateFeTargetKeywords(formatArray);

    const kwArray = store.feTargetKeywords?.map(item => {
      return {
        countryCode: item.countryCode,
        keyword: item.keyword,
        location: item.location,
        locationId: item.locationId,
        id: item.id,
      };
    });

    try {
      setIsKwNameEdit(false);

      await store?.editKeywords({...preparedKeyword}, 'edit');
      getCustomerQuota();
      setShowKwErrorMsg({
        showMessage: false,
        message: '',
      });
    } catch (e) {
      return Promise.reject(e);
    }

    if (callback) callback(kwArray);
  };


  /* When selecting location */
  const locationSelected = async (location: string, locationId: number, reach: number, countryCode: string) => {
    // let isMatch = false;
    // if we are in default location selection mode and if selected location doesnt match default location
    if (isEditDefaultLocation ) {
      if (!locationId && !countryCode) {
        store.updatelocationData('', '', null, null);
      }

      if (countryCode !== store.content.countryCode || locationId != store.content.locationId) {
        store.updatelocationData(countryCode, location, locationId, reach);
      }
    }

    // If we are not in default location selection mode, set location for that target keyword
    if (!isEditDefaultLocation) {
      // return if there is already a keyword with that name and that location
      // store.feTargetKeywords?.forEach(item => {
      //   if (item.keyword === kw.keyword && !item.locationId && !locationId ) {
      //     if (!item.countryCode && !countryCode || item.countryCode === countryCode) {
      //       isMatch = true;
      //       return;
      //     }
      //   };

      //   if (item.keyword === kw.keyword && item.locationId && locationId && item.locationId === locationId ) {
      //     isMatch = true;
      //     return;
      //   };
      // });
      // if (isMatch) return;
      // const itemIndex = store.feTargetKeywords.findIndex(item => {
      //   return item.keyword === kw.keyword && item.locationId === kw.locationId;
      // });
      const formatArray = [...store.feTargetKeywords];

      let keywordForFrontendArray;
      if (locationId && location) {
        keywordForFrontendArray = {
          keyword: kw.keyword,
          countryCode: '',
          locationId: locationId,
          location: location,
        };
      } else if (countryCode) {
        keywordForFrontendArray = {
          keyword: kw.keyword,
          countryCode: countryCode,
          locationId: null,
          location: null,
        };
      } else if (!locationId && !countryCode) {
        keywordForFrontendArray = {
          keyword: kw.keyword,
          countryCode: '',
          locationId: null,
          location: null,
        };
      }

      if (store.feTargetKeywords?.filter(kw => JSON.stringify({
        keyword: kw.keyword,
        countryCode: kw.countryCode,
        locationId: kw.locationId,
        location: kw.location,
      }) === JSON.stringify({
        keyword: keywordForFrontendArray.keyword,
        countryCode: keywordForFrontendArray.countryCode,
        locationId: keywordForFrontendArray.locationId,
        location: keywordForFrontendArray.location,
      })).length) {
        setShowKwErrorMsg({
          showMessage: true,
          message: 'This keyword already exists for selected location.',
        });
        return;
      }

      formatArray[idx] = keywordForFrontendArray;

      store.updateFeTargetKeywords(formatArray);

      // this mapping is important because we are preparing prop names to match api payload
      // const kwArray = store.feTargetKeywords?.map(item => {
      //   return {
      //     keyword: item.keyword,
      //     location: item.location,
      //     locationId: item.locationId,
      //   };
      // });

      setShowLocationSelector(false);
      setIsEditDefaultLocation(false);

      MixPanel.track(SCA_PAGE_UPDATE_KEYWORD_LOCATION, {
        'pageId': store.content.uuid,
        'targetKeyword': kw.keyword,
        'location': location || countryCode,
        'description': 'Changed location for target keyword',
      });

      try {
        await store?.editKeywords({keyword: kw.keyword, countryCode: countryCode, location: location, locationId: locationId, id: kw.id}, 'edit');
        getCustomerQuota();
      } catch (err) {
        return Promise.reject(err);
      }
    }
  };

  const locationDebouncerChange = debounce( async (country: string) => {
    try {
      await store?.updateCountriesList(country);
    } catch (err) {
      return Promise.reject(err);
    }
  }, 500);

  const openLocationSelector = () => {
    if (!showLocationSelector) {
      setIsEditDefaultLocation(false);
      setShowLocationSelector(true);
    } else {
      setShowLocationSelector(false);
    }
    // form.resetFields();
  };

  const searchForLocation = (location?: string) => {
    if (location && location !== '') {
      locationDebouncerChange(location);
    }
  };

  return (
    <div className={styles.keywordContainer} style={{width: isKwNameEdit && '255px'}}>
      {isKwNameEdit ? <>
        <Form className={styles.editKwForm} form={form} onFinish={onFinish} style={{display: isKwNameEdit && 'flex', flexDirection: isKwNameEdit ? 'row' : 'column'}}>
          <div className={styles.editKwInputContainer} >
            <Form.Item name='keyword' shouldUpdate >
              <Input inputType='text' variant='transparent' placeholder={t('new-keyword')} className={styles.customInputField}/>
            </Form.Item>
            {showKwErrorMsg.showMessage && <Text style={{display: 'block', fontSize: '12px', color: '#ff6262', maxWidth: '260px', whiteSpace: 'nowrap', textAlign: 'left'}}>{showKwErrorMsg.message}</Text>}
          </div>
          <div className={styles.editKwControllsContainer}>
            <Form.Item shouldUpdate>
              <Button
                buttonType='transparent'
                type='submit'
                className={classNames(styles.populateButton, styles.confirmButton)}
                disabled={store?.feTargetKeywords?.filter(kw => kw?.keyword.toLowerCase() == updateKeyword?.trim().toLowerCase())?.length}
              >
                <FontAwesomeIcon icon={faCheck} />
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                buttonType='transparent'
                className={classNames(styles.populateButton, styles.cancelButton)}
                onClick={() => {
                  form.resetFields();
                  setShowKwErrorMsg({
                    showMessage: false,
                    message: '',
                  });
                  setIsKwNameEdit(false);
                }}
                // disabled={formIsComplete.length <= 0}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </Form.Item>
          </div>
        </Form>
      </> :
        <>
          <div className={styles.keywordContainerTitleContainer}>
            <div>
              <div style={{display: 'flex', marginRight: '10px', flexDirection: 'column'}}>

                <Tooltip title={<><span style={{color: '#E8E8E8'}}>Precise Location:</span><span style={{color: '#fff', marginLeft: 5}}>{kw.location ? kw.location : kw.countryCode ? getSpecificCountryByCode(kw.countryCode.toUpperCase())?.name : 'Worldwide'}</span></>}
                  placement={'top'}
                  overlayInnerStyle={{backgroundColor: '#121212', borderRadius: 5, width: 'max-content'}}>
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    <div className={styles.keywordName} onClick={() => isPublic ? {} : kwClickHandler(kw.keyword)}>{kw.keyword}</div>
                    <span style={{marginLeft: 5}}>
                      <FontAwesomeIcon icon={faLocationDot} color='#4E5156' fontSize={12}/>
                    </span>
                  </div>
                </Tooltip>


                {/* <span style={{fontSize: '10px', color: '#4E5156', margin: '2px 0 0 0', maxWidth: '240px', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                  {kw.locationId ? kw.locationId === 2840 ? 'United States' : kw.location : kw.countryCode ? countries[kw.countryCode]?.name ? countries[kw.countryCode]?.name : kw.countryCode : (<><FontAwesomeIcon icon={faGlobe} size='sm'/>{` Worldwide`}</>)}
                </span> */}
                {/* {kw.serpTaskStatus && kw.serpTaskStatus === 'SUCCESS' || kw.serpTaskStatus === 'FAILURE' ?
                  <FontAwesomeIcon onClick={() => kwClickHandler(kw.keyword)} icon={faExternalLinkSquare} className={styles.keywordDrawerIcon} /> : null} */}
              </div>

            </div>
            {!isPublic &&
            <div className={styles.keywordEditIcons}>
              {/* <div className={styles.keywordDetailsButtonContainer} onClick={() => kwClickHandler(kw.keyword)}>
                <FontAwesomeIcon
                  icon={faChartBar}
                  className={styles.keywordDetailsIcon}/>
              </div> */}

              <Popover
                overlayClassName={styles.countrySelectorPopover}
                trigger='click'
                placement='bottomRight'
                mouseLeaveDelay={0}
                mouseEnterDelay={0.5}
                openClassName={styles.openPopover}
                // onVisibleChange={visible => setIsLocationPopoverVisible(visible)}
                visible={showLocationSelector}
                content={
                  <div style={{color: '#fff'}} ref={popRef}>
                    {isEditDefaultLocation && <>
                      <FontAwesomeIcon style={{color: 'black'}} icon={faArrowLeft} onClick={e => {
                        e.stopPropagation();
                        setIsEditDefaultLocation(false);
                      }}/>
                      <span style={{color: '#000', marginLeft: '10px'}}>SELECT DEFAULT LOCATION</span>
                    </>}
                    <Form
                      name='locations'
                      onFinish={onFinish}
                    >
                      <Form.Item
                        name='location-search'
                      >
                        <div style={{display: 'flex'}}>
                          <span style={{margin: 'auto'}}><FontAwesomeIcon style={{color: '#A3A4A4', marginRight: '5px'}} icon={faSearch}/></span>
                          <CommonInput
                            name='1'
                            onChange={e => searchForLocation(e.target.value)}
                            className={styles.customInput}
                            inputType='text'
                            variant='transparent'
                            placeholder={'Search for location'} />
                          {!isEditDefaultLocation && <Tooltip placement='bottom' title={'You can target the same keyword for multiple locations.'} trigger='click'>
                            <span style={{margin: 'auto'}}><FontAwesomeIcon style={{color: '#A3A4A4', marginRight: '5px'}} icon={faInfoCircle}/></span>
                          </Tooltip>}
                        </div>
                      </Form.Item>
                    </Form>
                    <div className={styles.divider}></div>
                    <div>
                      <Row className={styles.columnTitlesRow}>
                        <Col className={styles.columnTitle}>LOCATION</Col>
                        <Col className={styles.columnTitle}>REACH</Col>
                      </Row>
                      <>
                        <Row style={{justifyContent: 'space-between'}}>
                          <Col style={{color: '#000', maxWidth: '350px'}}>
                            {store.content.location || getSpecificCountryByCode(store.content.countryCode)?.name || store.content.countryCode ||
                              (<span><FontAwesomeIcon icon={faGlobe}/> Worldwide</span>)}
                          </Col>
                          <Col style={{color: '#000'}}>{formatNumber(store.content.reach)}</Col>
                        </Row>
                        <span className={styles.defaultLocationName} style={{color: 'gray'}}>{`Default location. `}</span>{!isEditDefaultLocation && <span style={{color: '#88ace0', cursor: 'pointer'}} onClick={e => {
                          e.stopPropagation();
                          setIsEditDefaultLocation(true);
                        }}>Change?</span>}
                      </>
                      <div className={styles.divider}></div>
                      <Row
                        className={classNames(
                          styles.singleCountryRow,
                          isEditDefaultLocation && !store.content.locationId && !store.content.countryCode || !isEditDefaultLocation && !kw.locationId && !kw.countryCode ? styles.singleCountryRowSelected : null)}
                        onClick={() => {
                          // if this location in list matches either default or current kw location, disable onClickEvent
                          if (isEditDefaultLocation && (store.content.locationId || store.content.countryCode) || !isEditDefaultLocation) {
                            locationSelected('', null, null, '');
                          }
                        }}>
                        <Col className={styles.locationInfo}>
                          <span><FontAwesomeIcon icon={faGlobe}/> Worldwide</span>
                          {isEditDefaultLocation && !store.content.locationId || !isEditDefaultLocation && !kw.locationId && !kw.countryCode ? <span><FontAwesomeIcon style={{color: '#7f4ead', marginLeft: '5px'}} icon={faCheck}/></span> : null}
                          {isEditDefaultLocation && !store.content.locationId ? <p className={styles.defaultLocationName}>Default location.</p> : null}
                        </Col>
                        <Col style={{color: '#000'}} className={classNames(styles.locationInfo, styles.setAsDefaultText, isEditDefaultLocation && store.content.locationId && styles.isEditDefaultLocation)}>{'Set as default'}</Col>
                      </Row>
                      {store?.countries && store?.countries.length ? store.countries.map((country, id) => {
                        return <Row key={id}
                          className={classNames(
                            styles.singleCountryRow,
                            isEditDefaultLocation && store.content.locationId === country.locationId || !isEditDefaultLocation && kw.locationId && kw.locationId === country.locationId ? styles.singleCountryRowSelected : null)}
                          onClick={() => {
                            // if this location in list matches either default or current kw location, disable onClickEvent
                            if (isEditDefaultLocation && store.content.locationId !== country.locationId || !isEditDefaultLocation && country.locationId === 2840 || country.locationId !== kw.locationId) {
                              locationSelected(country.location, country.locationId === 2840 ? null : country.locationId, country.reach, country.locationId === 2840 ? 'US' : country.countryCode);
                            }
                          }}>
                          <Col className={styles.locationInfo}>
                            {country.location}
                            {isEditDefaultLocation && store.content.locationId === country.locationId || !isEditDefaultLocation && kw.locationId && kw.locationId === country.locationId ? <span><FontAwesomeIcon style={{color: '#7f4ead', marginLeft: '5px'}} icon={faCheck}/></span> : null}
                            {isEditDefaultLocation && store.content.locationId === country.locationId ? <p className={styles.defaultLocationName}>Default location.</p> : null}
                          </Col>
                          <Col style={{color: '#000'}} className={classNames(styles.locationInfo, styles.countryReach, isEditDefaultLocation && store.content.locationId !== country.locationId && styles.isEditDefaultLocation)}>{formatNumber(country.reach)}</Col>
                          <Col style={{color: '#000'}} className={classNames(styles.locationInfo, styles.setAsDefaultText, isEditDefaultLocation && store.content.locationId !== country.locationId && styles.isEditDefaultLocation)}>{'Set as default'}</Col>
                        </Row>;
                      }) : <Row
                        className={classNames(
                          styles.singleCountryRow,
                          isEditDefaultLocation && store.content.locationId == 2840 || !isEditDefaultLocation && kw.locationId == 2840 ? styles.singleCountryRowSelected : null)}
                        onClick={() => {
                          // if this location in list matches either default or current kw location, disable onClickEvent
                          if (isEditDefaultLocation && (store.content.locationId != 2840) || !isEditDefaultLocation) {
                            locationSelected('', null, null, 'US');
                          }
                        }}>
                        <Col className={styles.locationInfo}>
                          <span>United States</span>
                          {isEditDefaultLocation && store.content.locationId == 2840 || !isEditDefaultLocation && kw.locationId == 2840 ? <span><FontAwesomeIcon style={{color: '#7f4ead', marginLeft: '5px'}} icon={faCheck}/></span> : null}
                        </Col>
                        <Col style={{color: '#000'}} className={classNames(styles.locationInfo, styles.setAsDefaultText, isEditDefaultLocation && store.content.locationId != 2840 && styles.isEditDefaultLocation)}>{'Set as default'}</Col>
                      </Row>}

                    </div>

                  </div>
                }
              >
                <div className={styles.keywordLocationIconContainer}
                  onClick={e => {
                    e.stopPropagation();
                    openLocationSelector();
                  }}>
                  <FontAwesomeIcon
                    icon={faMapMarkerAlt}
                    className={styles.keywordLocationIcon}
                    style={{display: showLocationSelector && 'block'}}/>
                </div>
              </Popover>
              {/* eslint-disable-next-line */}
              {kw.serpTaskStatus && kw.serpTaskStatus === 'SUCCESS' || kw.serpTaskStatus === 'FAILURE' ?
                <div className={styles.editKwButtonContainer} onClick={() => {
                  setShowLocationSelector(false);
                  setIsKwNameEdit(true);
                  form.setFieldsValue(kw);
                }}>
                  <FontAwesomeIcon
                    icon={faPen}
                    className={styles.editKwButtonIcon} />
                </div> : null}

              <div className={styles.removeKwButtonContainer} onClick={() => removeTargetKeywords()}>
                <FontAwesomeIcon
                  icon={faTrash}
                  className={styles.removeKwButtonIcon} />
              </div>
            </div>}


          </div>
          {kw.serpTaskStatus && kw.serpTaskStatus === 'FAILURE' ?
            <p style={{color: '#8c8d8e'}}>{t('failed-to-load')}</p> :
            <div className={styles.keywordMetric}>
              <Popover
                overlayClassName={styles.focusTermPopover}
                trigger='click'
                placement='left'
                mouseLeaveDelay={0}
                mouseEnterDelay={0.5}
                openClassName={styles.openPopover}
                // visible={true}
                content={
                  <div style={{color: '#ddd'}}>
                    {/* <span style={{color: '#fff', fontWeight: 600}}>
                  {t('search-volume')}</span> - {t('global-monthly')}<br/> */}
                    {/* {t('search-volume-keyword')}<br/> */}
                    <div className={styles.popoverTitle}>
                      <span className={styles.title}>Location</span>
                      <span className={styles.sv}>SV</span>
                    </div>
                    {store.content.countryCode && store.content.countryCode != '' && <><span className={styles.popoverCountrySv}>
                      <span className={styles.emoji}>
                        <span style={{marginRight: '5px', fontSize: '12px'}}>
                          <Flag width={12} code={store.content.countryCode}></Flag>
                        </span>
                        <span>
                          {store.content.countryCode == 'US' ? 'US' : store.content.countryCode == 'GB' ? 'UK' : store.content.countryCode}
                        </span>
                      </span>
                      <span className={styles.sv}>
                        {searchVolumeFormatter(kw?.sv)}
                      </span>
                    </span>
                    <div className={styles.divider}></div></>}
                    <span className={styles.popoverCountrySv}>
                      <span>
                        <Globe style={{width: '12px', marginRight: '5px'}} />Worldwide
                      </span>
                      <span className={styles.sv}>
                        {searchVolumeFormatter(kw?.globalSv)}
                      </span>
                    </span>
                    {kw.svTopCountries != null && kw.svTopCountries && kw.svTopCountries.map((sv, indx) => {
                      const _countryCode = sv?.countryCode.toUpperCase();
                      if (_countryCode != '') {
                        return (
                          <span key={indx} className={styles.popoverCountrySv}>
                            <span className={styles.emoji}>
                              {_countryCode != '' && _countryCode != store.content.countryCode && <span style={{marginRight: '5px', fontSize: '12px'}}>{<Flag width={12} code={_countryCode}></Flag>}</span>}

                              {_countryCode != '' && _countryCode != store.content.countryCode && (<span> {_countryCode == 'US' ? 'US' : _countryCode == 'GB' ? 'UK' : getSpecificCountryByCode(_countryCode)?.name}</span>)}
                            </span>
                            {sv?.countryCode != '' && _countryCode != store.content.countryCode && <span className={styles.sv}> {searchVolumeFormatter(sv?.searchVolume)} </span>}
                          </span>
                        );
                      }
                    })}
                  </div>
                }
              >

                {kw.serpTaskStatus && kw.serpTaskStatus === 'SUCCESS' ?
                  <div style={{display: 'flex', marginRight: '5px'}}>
                    <div className={styles.keywordMetricContainer}>
                      <Text className={styles['secondary-text']}>
                        SV: {searchVolumeFormatter(kw?.sv)}
                      </Text>
                    </div>

                    <Progress type='circle'
                      className={styles.keywordMetricProgressBar}
                      width={10}
                      showInfo={false}
                      strokeWidth={15}
                      percent={Math.round((kw?.globalSv || 0) * 100 / 15000) || 0}
                      trailColor='#e8e8e8'
                      strokeColor={getProgressColor(kw?.globalSv, 200, 500, 5000)}
                    />
                    <CaretDownOutlined size={10} style={{fontSize: '10px', color: 'inherit', marginTop: '7px', marginBottom: 'auto'}} />
                  </div> :
                  <div style={{width: '30%'}}> <SkeletonHorizontalLoaderGray lightLoader={true} height='24px' skeletonHeight='10px' width='100%' skeletonMarginTop='5px' /></div>
                }
              </Popover>
              <Popover
                overlayClassName={styles.focusTermPopover}
                trigger='hover'
                placement='bottom'
                mouseLeaveDelay={0}
                mouseEnterDelay={0.5}
                content={
                  <div style={{color: '#ddd'}}>
                    <span style={{color: '#fff', fontWeight: 600}}>{t('keyword-difficulty')}</span> - {`${t('score-from')} 
                    ${t('how-difficulty-rank')} 
                    ${t('organic-search-results')} 
                    ${t('authoritativeness')} 
                    ${t('first-page-google')}`}
                  </div>
                }
              >
                {kw.serpTaskStatus && kw.serpTaskStatus === 'SUCCESS' ?
                  <div style={{display: 'flex', marginRight: '5px'}}>
                    <div className={styles.keywordMetricContainer}>
                      <Text className={styles['secondary-text']}>
                        {t('kd')}: {kw?.kd ?? '-'}
                      </Text>
                    </div>
                    <Progress type='circle'
                      className={styles.keywordMetricProgressBar}
                      width={10}
                      showInfo={false}
                      strokeWidth={15}
                      percent={kw?.kd ?? 0}
                      trailColor='#e8e8e8'
                      strokeColor={getInverseProgressColor(kw?.kd, 25, 75)}
                    />
                  </div> : <div style={{width: '30%'}}><SkeletonHorizontalLoaderGray lightLoader={true} height='24px' skeletonHeight='10px' width='100%' skeletonMarginTop='5px' /></div>}
              </Popover>
              <Popover
                overlayClassName={styles.focusTermPopover}
                trigger='hover'
                placement='bottom'
                mouseLeaveDelay={0}
                mouseEnterDelay={0.5}
                content={
                  <div style={{color: '#ddd'}}>
                    <span style={{color: '#fff', fontWeight: 600}}>{t('cost-per-click')}</span> - {`${t('average-cost')} ${t('would-pay-google')}`}
                  </div>
                }
              >
                {kw.serpTaskStatus && kw.serpTaskStatus === 'SUCCESS' ?
                  <div style={{display: 'flex'}}>
                    <div className={styles.keywordMetricContainer}>
                      <Text className={styles['secondary-text']}>
                        {t('cpc')}: {costPerClickFormatter(kw?.cpc)}
                      </Text>
                    </div>

                    <Progress type='circle'
                      className={classNames(styles.keywordMetricProgressBar, styles.cpcMetricsProgressBar)}
                      width={10}
                      showInfo={false}
                      strokeWidth={15}
                      percent={Math.round(((kw?.cpc || 0) * 100) / 15) || 0}
                      trailColor='#e8e8e8'
                      strokeColor={'#34AEf3'}
                    /></div> :
                  <div style={{width: '30%'}}><SkeletonHorizontalLoaderGray lightLoader={true} height='24px' skeletonHeight='10px' width='100%' skeletonMarginTop='5px' /></div>}
              </Popover>
            </div>}
        </>}
    </div>
  );
});
